import { NavLink, Link, useLocation } from "react-router-dom";
import { useState } from "react";
import { Menu, X, ArrowUpRight } from "lucide-react";

const LOGO = "/ngxp.gif";

const links = [
  { to: "/", label: "Home", end: true },
  { to: "/about", label: "About Us" },
  { to: "/services", label: "Services" },
  { to: "/industries", label: "Industries" },
  { to: "/blogs", label: "Blogs" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  const scrollTopIfHome = () => {
    if (pathname === "/") window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <header
      data-testid="site-navbar"
      className="fixed top-0 left-0 right-0 z-50 bg-[#0A0B10] text-white rounded-b-[48px] shadow-[0_18px_50px_-15px_rgba(0,0,0,0.4)]"
    >
      <div className="container-ngxp flex items-center justify-between h-24 md:h-28">
        {/* Logo only — wordmark already inside the image */}
        <Link to="/" className="flex items-center group pl-2 md:pl-4" data-testid="nav-logo-link" onClick={scrollTopIfHome}>
          <img
            src={LOGO}
            alt="NGXP Technologies"
            className="h-28 md:h-32 w-auto object-contain"
          />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1" aria-label="Primary">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              onClick={l.to === "/" ? scrollTopIfHome : undefined}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `relative px-4 py-2 text-sm font-medium rounded-full transition-colors ${
                  isActive ? "text-white" : "text-white/65 hover:text-white"
                }`
              }
            >
              {({ isActive }) => (
                <span className="relative inline-flex flex-col items-center">
                  {l.label}
                  {isActive && (
                    <span className="absolute -bottom-2 h-[3px] w-7 rounded-full gradient-underline" />
                  )}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        {/* CTA + mobile toggle */}
        <div className="flex items-center gap-3">
          <Link
            to="/contact#contact-form"
            data-testid="nav-cta-discuss"
            className="cta-animated hidden md:inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold"
          >
            Let’s Talk <ArrowUpRight className="w-4 h-4" />
          </Link>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-full border border-white/10 hover:bg-white/5"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden border-t border-white/10" data-testid="nav-mobile-panel">
          <nav className="container-ngxp py-4 flex flex-col gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                onClick={() => { setOpen(false); if (l.to === "/") window.scrollTo({ top: 0, behavior: "smooth" }); }}
                data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-white/10 text-white"
                      : "text-white/70 hover:bg-white/5 hover:text-white"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/contact#contact-form"
              onClick={() => setOpen(false)}
              data-testid="nav-mobile-cta"
              className="cta-animated mt-2 inline-flex items-center justify-center gap-1.5 rounded-full px-4 py-3 text-sm font-semibold"
            >
              Let’s Talk <ArrowUpRight className="w-4 h-4" />
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
